/**
 * File parsing utilities for Excel and CSV import
 * Uses xlsx library for Excel files
 */

import type { ImportMapping, ImportPreview, LineItem, Quarter } from '../types/financials.types'
import { validateImportFile } from './validation'

/**
 * Parse Excel or CSV file
 */
export const parseFile = async (file: File): Promise<ImportPreview> => {
	const fileExtension = file.name.split('.').pop()?.toLowerCase()

	if (fileExtension === 'xlsx' || fileExtension === 'xls') {
		return parseExcelFile(file)
	} else if (fileExtension === 'csv') {
		return parseCSVFile(file)
	} else {
		throw new Error(`Unsupported file type: ${fileExtension}. Please upload .xlsx or .csv files.`)
	}
}

/**
 * Parse Excel file using xlsx library
 */
const parseExcelFile = async (file: File): Promise<ImportPreview> => {
	try {
		// Dynamically import xlsx to reduce bundle size
		const XLSX = await import('xlsx')

		return new Promise((resolve, reject) => {
			const reader = new FileReader()

			reader.onload = e => {
				try {
					const data = new Uint8Array(e.target?.result as ArrayBuffer)
					const workbook = XLSX.read(data, { type: 'array' })

					// Get first sheet
					const firstSheetName = workbook.SheetNames[0]
					const worksheet = workbook.Sheets[firstSheetName]

					// Convert to JSON array
					const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null })

					const preview = processImportData(jsonData as any[][], file.name, file.size)
					resolve(preview)
				} catch (error) {
					reject(new Error(`Failed to parse Excel file: ${error}`))
				}
			}

			reader.onerror = () => reject(new Error('Failed to read file'))
			reader.readAsArrayBuffer(file)
		})
	} catch (error) {
		throw new Error(`Excel parsing error: ${error}`)
	}
}

/**
 * Parse CSV file
 */
const parseCSVFile = async (file: File): Promise<ImportPreview> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader()

		reader.onload = e => {
			try {
				const text = e.target?.result as string
				const data = parseCSVText(text)
				const preview = processImportData(data, file.name, file.size)
				resolve(preview)
			} catch (error) {
				reject(new Error(`Failed to parse CSV file: ${error}`))
			}
		}

		reader.onerror = () => reject(new Error('Failed to read file'))
		reader.readAsText(file)
	})
}

/**
 * Parse CSV text into 2D array
 */
const parseCSVText = (text: string): any[][] => {
	const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0)
	const result: any[][] = []

	for (const line of lines) {
		// Simple CSV parsing (handles quoted fields)
		const row: any[] = []
		let currentField = ''
		let inQuotes = false

		for (let i = 0; i < line.length; i++) {
			const char = line[i]

			if (char === '"') {
				if (inQuotes && line[i + 1] === '"') {
					// Escaped quote
					currentField += '"'
					i++
				} else {
					// Toggle quote mode
					inQuotes = !inQuotes
				}
			} else if (char === ',' && !inQuotes) {
				// End of field
				row.push(parseValue(currentField.trim()))
				currentField = ''
			} else {
				currentField += char
			}
		}

		// Add last field
		row.push(parseValue(currentField.trim()))
		result.push(row)
	}

	return result
}

/**
 * Parse a CSV field value (string to appropriate type)
 */
const parseValue = (value: string): any => {
	if (value === '' || value === null || value === undefined) {
		return null
	}

	// Try to parse as number
	const cleaned = value.replace(/[$,\s]/g, '')
	const num = parseFloat(cleaned)
	if (!isNaN(num) && cleaned !== '') {
		return num
	}

	return value
}

/**
 * Process imported data into ImportPreview
 */
const processImportData = (data: any[][], fileName: string, fileSize: number): ImportPreview => {
	console.log('=== PROCESSING IMPORT DATA ===')
	console.log('Raw data rows:', data.length)
	console.log('First row (headers):', data[0])
	console.log('Sample data row:', data[1])
	
	// Validate file structure
	const validation = validateImportFile(data, 5)
	console.log('Validation result:', validation)

	if (!validation.isValid) {
		console.error('Validation failed:', validation.errors)
		throw new Error(`Invalid file structure:\n${validation.errors.join('\n')}`)
	}

	// Extract headers and data
	const headers = data[0]
	const dataRows = data.slice(1)

	// Identify quarters from headers (skip first column which is line item label)
	const quarterHeaders = headers.slice(1).filter(h => h && h.toString().trim().length > 0)

	// Create line item mappings
	const mappings: ImportMapping[] = dataRows
		.filter(row => row[0] && row[0].toString().trim().length > 0)
		.map((row, index) => {
			const lineItemLabel = row[0].toString().trim()
			const toLineItem = fuzzyMatchLineItem(lineItemLabel)

			return {
				fromFile: lineItemLabel,
				toLineItem: toLineItem || `custom_${index}`,
				action: toLineItem ? 'update' : 'create',
				isMatched: !!toLineItem,
			}
		})

	return {
		fileName,
		fileSize,
		lineItemsFound: dataRows.length,
		quartersFound: quarterHeaders.length,
		mappings,
		warnings: validation.warnings,
		errors: validation.errors,
		rawData: data, // Include raw data for later conversion
	}
}

/**
 * Fuzzy match a line item label to known line item IDs
 */
export const fuzzyMatchLineItem = (label: string): string | null => {
	const normalized = label.toLowerCase().trim()

	// Exact matches
	const exactMatches: Record<string, string> = {
		sales: 'sales',
		revenue: 'sales',
		'total sales': 'sales',
		'total revenue': 'sales',
		cogs: 'cogs',
		'cost of goods sold': 'cogs',
		'cost of sales': 'cogs',
		'gross profit': 'gross_profit',
		'gross margin': 'gross_profit',
		expenses: 'expenses',
		'operating expenses': 'expenses',
		'total expenses': 'expenses',
		earnings: 'earnings',
		'net income': 'earnings',
		'net profit': 'earnings',
		profit: 'earnings',
		rent: 'rent',
		'rent expense': 'rent',
		'lease expense': 'rent',
		utilities: 'utilities_electric',
		electric: 'utilities_electric',
		electricity: 'utilities_electric',
		labor: 'labor_costs',
		'labor costs': 'labor_costs',
		'labor expense': 'labor_costs',
		wages: 'labor_costs',
		'payroll': 'labor_costs',
		// A property manager's statement (MYD-652)
		'rental revenue': 'sales',
		'rental income': 'sales',
		'total rental revenue': 'sales',
		'total property revenue': 'sales',
		'total income': 'sales',
		'gross potential rent': 'sales',
		vacancy: 'cogs',
		'vacancy & credit loss': 'cogs',
		'vacancy and credit loss': 'cogs',
		'vacancy loss': 'cogs',
		'credit loss': 'cogs',
		'effective gross income': 'gross_profit',
		egi: 'gross_profit',
		'total operating expenses': 'expenses',
		'net operating income': 'earnings',
		noi: 'earnings',
		'base rent': 'base_rent',
		'minimum rent': 'base_rent',
		'fixed rent': 'base_rent',
		'percentage rent': 'percentage_rent',
		'overage rent': 'percentage_rent',
		'management fee': 'property_management_fee',
		'management fees': 'property_management_fee',
		'property management fee': 'property_management_fee',
		landscaping: 'landscaping',
		'parking lot maintenance': 'parking_lot_maintenance',
		'replacement reserves': 'replacement_reserves',
		reserves: 'replacement_reserves',
		'leasing commissions': 'leasing_commissions',
		'tenant improvements': 'tenant_improvements',
		'capital expenditures': 'capital_expenditures',
		capex: 'capital_expenditures',
		'debt service': 'debt_service',
	}

	if (exactMatches[normalized]) {
		return exactMatches[normalized]
	}

	// Partial matches.
	//
	// Owner rows come first. The checks below them read "rent" as rent EXPENSE
	// and "tax" as property tax expense, which is right for a retail operator and
	// exactly backwards for a landlord: "Base Rent" is income, and "Tax
	// Recoveries" is money coming in, not going out.
	if (normalized.includes('recover') || normalized.includes('reimburs')) {
		if (normalized.includes('cam') || normalized.includes('common')) return 'cam_recoveries'
		if (normalized.includes('tax')) return 'tax_recoveries'
		if (normalized.includes('insurance')) return 'insurance_recoveries'
		return 'other_property_income'
	}
	if (normalized.includes('percentage rent')) return 'percentage_rent'
	if (/\b(base|minimum|fixed|scheduled) rent\b/.test(normalized)) return 'base_rent'
	if (normalized.includes('net operating income')) return 'earnings'
	if (normalized.includes('vacancy')) return 'cogs'
	if (normalized.includes('management fee')) return 'property_management_fee'

	if (normalized.includes('rent') || normalized.includes('lease')) return 'rent'
	if (normalized.includes('utilit')) return 'utilities_electric'
	if (normalized.includes('labor') || normalized.includes('wage')) return 'labor_costs'
	if (normalized.includes('market') || normalized.includes('advertis')) return 'marketing_advertising'
	if (normalized.includes('insurance')) return 'insurance_property'
	if (normalized.includes('tax') && !normalized.includes('income')) return 'property_taxes'

	return null
}

/**
 * Convert imported data to line items and quarters
 */
export const convertImportToIncomeStatement = (
	data: any[][],
	mappings: ImportMapping[],
	existingQuarters: Quarter[]
): { lineItems: Partial<LineItem>[]; quarters: Quarter[] } => {
	const headers = data[0]
	const dataRows = data.slice(1)

	// Extract quarter labels from headers
	const quarterLabels = headers.slice(1).map(h => (h ? h.toString().trim() : ''))

	// Use existing quarters or create new ones
	const quarters: Quarter[] =
		quarterLabels.length === existingQuarters.length
			? existingQuarters
			: quarterLabels.map((label, index) => ({
					id: `q${index + 1}`,
					label: label || `Q${index + 1}`,
					order: index + 1,
			  }))

	// Convert data rows to line items
	const lineItems: Partial<LineItem>[] = dataRows
		.filter((row, index) => mappings[index])
		.map((row, index) => {
			const mapping = mappings[index]
			const values = row
				.slice(1)
				.map((value, qIndex) => ({
					quarter: quarters[qIndex]?.id || `q${qIndex + 1}`,
					value: typeof value === 'number' ? value : 0,
				}))
				.filter(v => quarters.find(q => q.id === v.quarter)) // Fixed: was checking quarters[v.quarter]

			return {
				id: mapping.toLineItem,
				label: mapping.fromFile,
				type: 'input' as const,
				category: 'other' as const,
				isDefault: false,
				order: index + 10, // Start after default items
				values,
			}
		})

	return { lineItems, quarters }
}

